import { Box, type BoxProps } from '@chakra-ui/react';
import type { Country } from '@/models/country';
import type { ReactNode } from 'react';
import { isClickableTeamCountry, type TeamDetailSource } from './teamDetailUtils';
import { useTeamDetailOptional } from './TeamDetailContext';

/** Keeps fixture / teams-page / profile rows visually unchanged on hover. */
const noHoverBackground = {
  _hover: { bg: 'unset' },
  _active: { bg: 'unset' },
} as const;

export function TeamDetailTrigger(props: {
  country: Country;
  source?: TeamDetailSource;
  rank?: number;
  children: ReactNode;
  disabled?: boolean;
  /** `row` = full-width list row; `compact` = shrink-wrapped match-row name; `inline` = flag-sized. */
  variant?: 'row' | 'compact' | 'inline';
  /** When set, replaces the default openTeamDetail click handler (parent modal handoff). */
  onOpen?: () => void;
} & Omit<BoxProps, 'children' | 'onClick'>) {
  const teamDetail = useTeamDetailOptional();
  const canOpen = !props.disabled && isClickableTeamCountry(props.country) && teamDetail != null;

  const { country, source, rank, children, disabled: _disabled, variant = 'inline', onOpen, ...rest } = props;

  const variantLayoutProps: BoxProps =
    variant === 'row'
      ? { minW: 0, overflow: 'hidden', w: 'full' }
      : variant === 'compact'
        ? { display: 'inline-flex', alignItems: 'center', w: 'fit-content', maxW: '100%', minW: 0, overflow: 'hidden' }
        : {};

  if (!canOpen) {
    return (
      <Box
        as='span'
        display={variant === 'row' ? 'block' : variant === 'compact' ? 'inline-flex' : 'contents'}
        w={variant === 'row' ? 'full' : variant === 'compact' ? 'fit-content' : undefined}
        maxW={variant === 'compact' ? '100%' : undefined}
        minW={variant === 'compact' ? 0 : undefined}
        overflow={variant === 'compact' ? 'hidden' : undefined}
        {...variantLayoutProps}
        {...rest}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      as='button'
      type='button'
      display={variant === 'row' ? 'flex' : variant === 'compact' ? 'inline-flex' : 'contents'}
      w={variant === 'row' ? 'full' : variant === 'compact' ? 'fit-content' : undefined}
      maxW={variant === 'compact' ? '100%' : undefined}
      textAlign='left'
      cursor='pointer'
      borderRadius={variant === 'row' || variant === 'compact' ? 'md' : undefined}
      alignItems={variant === 'compact' ? 'center' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (onOpen) {
          onOpen();
        } else {
          teamDetail.openTeamDetail(country, { source, rank });
        }
      }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400', outlineOffset: '2px' }}
      {...noHoverBackground}
      {...variantLayoutProps}
      {...rest}
    >
      {children}
    </Box>
  );
}
